import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { teamAPI } from '../../services/api';
import { Plus, Search, Edit, Trash2, Eye, Users } from 'lucide-react';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/Toast';
import { getImageUrl } from '../../utils/imageHelper';

const AdminTeam = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, member: null });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        fetchTeamMembers();
    }, [searchTerm]);

    const fetchTeamMembers = async () => {
        try {
            setLoading(true);
            const params = { includeInactive: 'true' };
            if (searchTerm) params.search = searchTerm;

            const response = await teamAPI.getAll(params);
            setTeamMembers(response.data);
        } catch (error) {
            console.error('Error fetching team members:', error);
            showToast('Failed to load team members', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await teamAPI.delete(deleteDialog.member.id);
            setTeamMembers(teamMembers.filter(m => m.id !== deleteDialog.member.id));
            showToast('Team member deleted successfully', 'success');
            setDeleteDialog({ isOpen: false, member: null });
        } catch (error) {
            console.error('Error deleting team member:', error);
            showToast('Failed to delete team member', 'error');
        }
    };

    const showToast = (message, type) => {
        setToast({ message, type });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
                    <p className="text-gray-600 mt-1">Manage your team members displayed on the About Us page</p>
                </div>
                <Link
                    to="/admin/team/new"
                    className="btn-primary inline-flex items-center space-x-2"
                >
                    <Plus className="h-5 w-5" />
                    <span>Add Team Member</span>
                </Link>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-lg shadow">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search team members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                    />
                </div>
            </div>

            {/* Team Members Grid */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
                    </div>
                ) : teamMembers.length === 0 ? (
                    <div className="text-center py-12">
                        <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600">No team members found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                        {teamMembers.map((member) => (
                            <div key={member.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                <div className="text-center">
                                    <img
                                        src={getImageUrl(member.image)}
                                        alt={member.name}
                                        className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                                    />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                                    <p className="text-earth-green font-medium mb-2">{member.role}</p>
                                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{member.bio}</p>

                                    {/* Status Badge */}
                                    <div className="mb-4">
                                        {member.active ? (
                                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                Inactive
                                            </span>
                                        )}
                                        <span className="ml-2 text-xs text-gray-500">Order: {member.order}</span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex justify-center space-x-2">
                                        <Link
                                            to={`/admin/team/${member.id}/edit`}
                                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                            title="Edit"
                                        >
                                            <Edit className="h-5 w-5" />
                                        </Link>
                                        <button
                                            onClick={() => setDeleteDialog({ isOpen: true, member })}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteDialog.isOpen}
                title="Delete Team Member"
                message={`Are you sure you want to delete "${deleteDialog.member?.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialog({ isOpen: false, member: null })}
            />

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AdminTeam;
